import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box, Typography, Button, Stack, Chip, Skeleton, Table, TableHead, TableRow,
    TableCell, TableBody, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
    MenuItem, IconButton, Tooltip, RadioGroup, Radio, FormControlLabel, FormLabel, FormControl,
    FormHelperText, Avatar,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CancelIcon from "@mui/icons-material/CancelOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopyOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import KeyIcon from "@mui/icons-material/VpnKeyOutlined";
import { toast } from "react-toastify";
import AppHeader from "../components/AppHeader";
import AppFooter from "../components/AppFooter";
import api, { apiError, tokens as session } from "../api";
import openCheckout from "../razorpayCheckout";
import { withGst } from "../gst";

/*
 * The page a customer lands on: the licences they have issued, one row each.
 *
 * A token is a plan granted to one FDM admin email. One account can hand out several - a
 * group with three sites, or an integrator looking after other people's plants - and each
 * sits on its own plan with its own daily allowance, so one busy site cannot eat
 * another's.
 *
 * Create asks for two things and nothing else. Edit changes the plan and the status: the
 * email is the plant's identity, and typing over it would move a live licence somewhere
 * else with no way back. There is no delete, for the same reason - Deactive is the
 * reversible version, and it is what stops a plant being served.
 */


const darkField = {
    "& .MuiOutlinedInput-root": {
        color: "#fff", borderRadius: 2,
        bgcolor: "rgba(255,255,255,.04)",
        "& fieldset": { borderColor: "rgba(255,255,255,.16)" },
        "&:hover fieldset": { borderColor: "rgba(255,255,255,.32)" },
        "&.Mui-focused fieldset": { borderColor: "#7BD95F" },
    },
    "& .MuiInputLabel-root": { color: "rgba(255,255,255,.5)" },
    "& .MuiInputLabel-root.Mui-focused": { color: "#7BD95F" },
    "& .MuiFormHelperText-root": { color: "rgba(255,255,255,.4)" },
    "& .MuiSelect-icon": { color: "rgba(255,255,255,.5)" },
    "& .Mui-disabled": { WebkitTextFillColor: "rgba(255,255,255,.45)" },
};

/* Section headings inside the dialogs: quiet, uppercase, and the same everywhere. */
const sectionLabel = {
    color: "rgba(255,255,255,.5)", fontSize: 11.5, fontWeight: 700,
    textTransform: "uppercase", letterSpacing: .6, mb: 1,
};

const dialogPaper = {
    sx: {
        bgcolor: "#0C1A12", color: "#fff", borderRadius: 3,
        border: "1px solid rgba(255,255,255,.12)", backgroundImage: "none",
    },
};

/* Green for served, plain grey for off - the row should say which it is without being
   read. Deactive is not an error, so it does not get a red. */
const statusChip = (status) => status === "active"
    ? { bgcolor: "rgba(46,125,32,.25)", color: "#9FE08C", border: "1px solid rgba(123,217,95,.35)", fontWeight: 600 }
    : { bgcolor: "rgba(255,255,255,.06)", color: "rgba(255,255,255,.55)", border: "1px solid rgba(255,255,255,.16)", fontWeight: 600 };

const planChip = (plan) => ({
    bgcolor: plan === "lite" ? "rgba(255,255,255,.08)" : "rgba(46,125,32,.25)",
    color: plan === "lite" ? "rgba(255,255,255,.75)" : "#9FE08C",
    border: "1px solid rgba(255,255,255,.14)", fontWeight: 600, textTransform: "capitalize",
});

/*
 * Two states, both worth seeing at once - a dropdown would hide whichever one is not
 * chosen, and "is this plant switched off?" is the question this field exists to answer.
 */
function StatusRadio({ value, onChange }) {
    const radio = {
        color: "rgba(255,255,255,.4)",
        "&.Mui-checked": { color: "#7BD95F" },
    };
    return (
        <FormControl>
            <FormLabel sx={{
                color: "rgba(255,255,255,.5)", fontSize: 13,
                "&.Mui-focused": { color: "rgba(255,255,255,.5)" },
            }}>Status</FormLabel>
            <RadioGroup row value={value} onChange={(e) => onChange(e.target.value)}>
                <FormControlLabel value="active" control={<Radio size="small" sx={radio} />}
                    label="Active" sx={{ color: "#fff" }} />
                <FormControlLabel value="inactive" control={<Radio size="small" sx={radio} />}
                    label="Deactive" sx={{ color: "#fff" }} />
            </RadioGroup>
            <FormHelperText sx={{ color: "rgba(255,255,255,.4)", mx: 0 }}>
                A deactive token stops that plant being answered
            </FormHelperText>
        </FormControl>
    );
}

export default function Account() {
    const navigate = useNavigate();
    const [account, setAccount] = useState(null);
    const [rows, setRows] = useState(null);

    const [createOpen, setCreateOpen] = useState(false);
    const [form, setForm] = useState({ email: "", plan: "lite", period: "monthly", status: "active" });
    const [busy, setBusy] = useState(false);

    const [editing, setEditing] = useState(null);     // the row being edited
    const [buying, setBuying] = useState(null);       // the row being upgraded
    const [stopping, setStopping] = useState(null);   // the row being asked about, before cancelling
    const [allPlans, setAllPlans] = useState([]);     // every plan, for the picker
    const [sellable, setSellable] = useState([]);     // the ones with a price on them
    const [choice, setChoice] = useState({ plan: "", period: "monthly" });
    const [newKey, setNewKey] = useState(null);       // { email, key } - shown once

    /*
     * A payment was started on this page, just now.
     *
     * The banner below hangs on this rather than on a row being "pending", because the
     * two are not the same thing: a link opened last week and walked away from leaves a
     * row pending for ever, and greeting somebody with "waiting for payment" every time
     * they open the page is a lie they learn to ignore. Lost on reload, deliberately -
     * it is about this moment, not about the account.
     */
    const [paying, setPaying] = useState(false);

    /*
     * Razorpay's publishable key, sent with the token list.
     *
     * Needed to reopen Checkout for a payment somebody walked away from - without it the
     * only way back is Razorpay's hosted page in another tab, which is the thing the
     * popup exists to avoid.
     */
    const [keyId, setKeyId] = useState(null);

    /** Finish a payment already started on a row: same popup, same fallback. */
    const resumePay = (r) => pay(
        { keyId, subscriptionId: r.billing?.subscriptionId, payUrl: r.billing?.payUrl },
        r.email,
        `${r.billing?.pendingPlan || r.plan} plan`,
    );

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/me/tokens");
            // Array.isArray, not `data.data`: a stale server answers this path with the
            // React index.html, and `undefined` here crashes the table on `.length`
            // rather than showing an empty list.
            setRows(Array.isArray(data?.data) ? data.data : []);
            if (data?.keyId) setKeyId(data.keyId);
        } catch (e) { toast.error(apiError(e)); setRows([]); }
    }, []);

    useEffect(() => {
        // What is on sale. Driven by the server, so a plan nobody has priced yet simply
        // does not appear rather than being offered at nothing.
        api.get("/plans")
            .then(({ data }) => {
                const rows = data.data || [];
                setAllPlans(rows);
                // A plan with no price is one nobody has priced yet, not a free one.
                setSellable(rows.filter((p) => p.code !== "lite" && p.priceMonthly > 0));
            })
            .catch(() => { setAllPlans([]); setSellable([]); });

        api.get("/me")
            .then(({ data }) => {
                // Signing up asks for an email and nothing else, so somebody can arrive
                // here with no name at all. They finish that on the profile first - every
                // invoice and every support reply needs a person to address.
                if (!data.account.name) { navigate("/profile"); return; }
                setAccount(data.account);
            })
            .catch(() => { session.account.clear(); navigate("/"); });
        load();
    }, [navigate, load]);

    /*
     * While a payment is in flight, keep asking.
     *
     * The plan moves when Razorpay's webhook reaches our server, which travels a
     * different road from the customer's browser and lands a second or two later. Without
     * this they come back to a page that still says Lite, and the only cure is a reload
     * they have no reason to think of.
     *
     * It gives up after a minute. A webhook that has not arrived by then is not late, it
     * is lost, and a page that polls for ever hides that from everybody.
     */
    const pending = (rows || []).some((r) => r.billing?.state === "pending");
    const [gaveUp, setGaveUp] = useState(false);
    const tries = useRef(0);

    useEffect(() => {
        // A fresh payment deserves a fresh minute.
        if (pending) { tries.current = 0; setGaveUp(false); }
    }, [pending]);

    useEffect(() => {
        if (!pending || gaveUp) return undefined;
        const id = setInterval(() => {
            if (tries.current >= 30) { setGaveUp(true); return; }
            tries.current += 1;
            load();
        }, 2000);
        return () => clearInterval(id);
    }, [pending, gaveUp, load]);

    /*
     * Say it out loud when the plan finally moves. The row changes quietly, and a
     * customer watching a spinner should be told the thing they paid for is on.
     */
    const seen = useRef({});
    useEffect(() => {
        if (!rows) return;
        rows.forEach((r) => {
            const state = r.billing?.state || "";
            if (seen.current[r.id] === "pending" && state === "active") {
                toast.success(`${r.email} is now on ${r.plan}`);
            }
            seen.current[r.id] = state;
        });
    }, [rows]);

    /*
     * Take the payment, then start watching.
     *
     * Everything that costs money ends here - creating a token on a paid plan, upgrading
     * one later - so the popup, the fallback and what we say afterwards are decided once.
     * Nothing is marked paid: `load()` simply picks the row back up, and the polling
     * above carries it the rest of the way when the webhook lands.
     */
    const pay = async (billing, email, description) => {
        setPaying(true);
        const outcome = await openCheckout({ ...billing, email, description });
        if (outcome === "dismissed") {
            setPaying(false);
            toast.info("Payment not finished - you can pay later from the Billing column");
        } else if (outcome === "failed") {
            setPaying(false);
            toast.error("The payment did not go through - you can try again from the Billing column");
        }
        load();
    };


    /*
     * A token is always created free, whatever plan was chosen.
     *
     * The plan is a purchase, and every purchase on this page goes through the same
     * checkout - the customer should see what they are buying and what it costs with tax
     * before anything is charged, whether they are upgrading an old plant or adding a new
     * one. So the token is created on Lite, the key is shown, and the chosen plan is
     * carried to the checkout from there.
     */
    const create = async () => {
        setBusy(true);
        try {
            const wanted = form.plan !== "lite" ? { plan: form.plan, period: form.period } : null;
            const { data } = await api.post("/me/tokens", { ...form, plan: "lite" });
            setCreateOpen(false);
            setForm({ email: "", plan: "lite", period: "monthly", status: "active" });

            /*
             * A paid plan goes straight to the checkout. Somebody who filled in a plan
             * and pressed Create has asked to buy it; stopping to admire the key first
             * is a step they did not ask for and a payment some of them will not come
             * back to. The key is not lost by moving on - it can be read from the row
             * whenever they want it.
             */
            if (wanted) {
                navigate("/checkout", {
                    state: { tokenId: data.token.id, email: data.token.email, ...wanted },
                });
                return;
            }

            // A free token has nothing to pay for, so the key is shown here and now -
            // this is the only moment it exists in readable form without asking again.
            setNewKey({ email: data.token.email, key: data.key });
            load();
        } catch (e) { toast.error(apiError(e)); }
        setBusy(false);
    };

    /*
     * Three different acts behind one Save.
     *
     * Status is an ordinary edit. A move up - Lite to Plus, Plus to Pro - is a purchase
     * and goes through Razorpay, exactly as the create dialog does; the server refuses a
     * plan written straight onto the token, so there is no way around it. A move down to
     * Lite is the end of a mandate, not a purchase, so it cancels and lets the paid
     * period run out.
     *
     * Status is saved first: whatever happens at Razorpay, that part is already done.
     */
    const saveEdit = async () => {
        setBusy(true);
        try {
            if (editing.status !== editing.currentStatus) {
                await api.patch(`/me/tokens/${editing.id}`, { status: editing.status });
            }

            const changed = planChanged || periodChanged;

            if (planChanged && editing.plan === "lite") {
                await api.post(`/me/tokens/${editing.id}/unsubscribe`);
                toast.success("Billing stopped - the plan runs to the end of the paid period");
            } else if (changed) {
                // A move up is a purchase, so it goes to the checkout like every other
                // purchase here rather than straight to a payment popup.
                const { id, email, plan, period } = editing;
                setEditing(null);
                navigate("/checkout", { state: { tokenId: id, email, plan, period: period || "monthly" } });
                return;
            } else {
                toast.success("Token updated");
            }

            setEditing(null);
            load();
        } catch (e) { toast.error(apiError(e)); }
        setBusy(false);
    };

    /*
     * The key is not in the table - only its last four characters are - so copying means
     * asking the server for it. Fetched on the click rather than loaded with the list, so
     * a screen left open does not sit there holding everybody's keys in the page.
     */
    const copyKey = async (id) => {
        try {
            const { data } = await api.get(`/me/tokens/${id}/key`);
            await copy(data.key);
        } catch (e) { toast.error(apiError(e)); }
    };

    /*
     * What a year saves against twelve months, worked out from the two prices.
     *
     * Not taken from the server's flat figure: a plan can carry a yearly price set by
     * hand, and then the saving is whatever those two numbers say rather than the
     * standard discount. It also read "Save undefined%" here, because the endpoint this
     * page loads from does not send that field at all.
     */
    const yearlySavingPct = (p) => {
        const full = (p?.priceMonthly || 0) * 12;
        if (!full || !p?.priceYearly || p.priceYearly >= full) return 0;
        return Math.round((1 - p.priceYearly / full) * 100);
    };

    // The plan being bought in the create dialog, or null for Lite.
    const paidPlan = sellable.find((p) => p.code === form.plan) || null;

    // The same, for the edit dialog, plus whether the plan actually moved.
    const editPaidPlan = sellable.find((p) => p.code === editing?.plan) || null;
    const planChanged = Boolean(editing) && editing.plan !== editing.currentPlan;

    /*
     * Switching monthly to yearly is a purchase too.
     *
     * It was not treated as one: only a change of plan counted, so the Billing choice
     * sat there looking like a control and did nothing when pressed. A customer moving
     * to yearly billing saved their token, saw "Token updated", and went on paying
     * monthly.
     *
     * Only on a paid plan - Lite has no billing period to change.
     */
    const periodChanged = Boolean(editing) && Boolean(editPaidPlan)
        && editing.plan !== "lite" && editing.period !== editing.currentPeriod;
    const editIsPurchase = planChanged || periodChanged;

    const openUpgrade = (row) => {
        /*
         * A cancelled plan with days left opens on itself - the common thing to want
         * there is to carry on, not to move up - and anything else opens on the first
         * plan that is not the current one.
         */
        const resumable = row.billing?.state !== "active"
            && row.billing?.paidTill && new Date(row.billing.paidTill) > new Date()
            && sellable.find((p) => p.code === row.plan);
        const first = resumable || sellable.find((p) => p.code !== row.plan) || sellable[0];
        setChoice({ plan: first?.code || "", period: "monthly" });
        setBuying(row);
    };

    const nothingToChange = Boolean(buying)
        && choice.plan === buying.plan
        && choice.period === (buying.billing?.period || "monthly")
        && buying.billing?.state === "active";

    /** A plan's price for a billing period, before tax. Zero means not sold that way. */
    const priceOf = (code, period) => {
        const p = sellable.find((x) => x.code === code);
        return (p && (period === "yearly" ? p.priceYearly : p.priceMonthly)) || 0;
    };

    /*
     * On to the checkout page, where the order is laid out and paid for.
     *
     * Nothing is created here - no Razorpay plan, no mandate, no change to the row. The
     * customer can read what they are buying and leave, and nothing is left behind.
     */
    const reviewOrder = () => {
        navigate("/checkout", {
            state: {
                tokenId: buying.id,
                email: buying.email,
                plan: choice.plan,
                period: choice.period,
            },
        });
    };

    const stopSubscription = async (row) => {
        setBusy(true);
        try {
            await api.post(`/me/tokens/${row.id}/unsubscribe`);
            // Two different acts behind one button, so two different answers: calling
            // off a change that has not happened is not the same as ending a plan.
            toast.success(scheduled(row)
                ? "The scheduled change has been called off - your current plan runs to the end of the paid period"
                : "Subscription cancelled - the plan runs to the end of the paid period");
            setStopping(null);
            load();
        } catch (e) { toast.error(apiError(e)); }
        setBusy(false);
    };

    const copy = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            toast.success("Copied");
        } catch {
            toast.error("Could not copy - select the text and copy it by hand");
        }
    };

    const th = {
    color: "rgba(255,255,255,.72)", fontSize: 12, fontWeight: 700,
    textTransform: "uppercase", letterSpacing: .6, whiteSpace: "nowrap",
    bgcolor: "rgba(255,255,255,.06)",
    borderBottom: "1px solid rgba(255,255,255,.18)",
};
    const td = { color: "#fff", borderColor: "rgba(255,255,255,.07)" };

    /*
     * The round buttons in the Actions column: the header's avatar, shrunk.
     *
     * Colour carries the weight, not the size - green for the ordinary edit, red for the
     * one that ends a paid plan, muted for the one that only reads. Same shape so they
     * read as one set.
     */
    const actionAvatar = (bg = "#2E7D20") => ({
        width: 28, height: 28, bgcolor: bg, color: "#fff",
    });

    /** A mandate already authorised, whose first charge is still ahead of it. */
    const scheduled = (r) => Boolean(r.billing?.startsAt) && new Date(r.billing.startsAt) > new Date();

    /** Is there still paid time left on this token? */
    const stillPaidTill = (d) => Boolean(d) && new Date(d) > new Date();

    // An invoice that has not been paid has no date on it yet.
    const fmtDate = (d) => (d
        ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
        : "-");

    return (
        <Box sx={{
            minHeight: "100vh", color: "#fff",
            display: "flex", flexDirection: "column",
            background:
                "radial-gradient(900px 520px at 12% 0%, rgba(46,125,32,.32), transparent 62%),"
                + "radial-gradient(760px 540px at 88% 100%, rgba(20,90,60,.28), transparent 64%), #07120B",
        }}>
            <AppHeader account={account} />

            <Box sx={{ py: { xs: 3, md: 5 }, px: { xs: 2, md: 4 } }}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}
                    sx={{ justifyContent: "space-between", alignItems: { sm: "center" }, mb: 3 }}>
                    <Box>
                        <Typography sx={{ fontWeight: 800, fontSize: 26, letterSpacing: -0.5 }}>Tokens</Typography>
                        <Typography sx={{ color: "rgba(255,255,255,.6)", fontSize: 14.5, mt: .5 }}>
                            One token per plant. Create it with that plant's SCADA admin email.
                        </Typography>
                    </Box>
                    {/* Outlined, the same shape the admin pages use for the action that
                        opens a dialog - one button style across the console. */}
                    <Button startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}
                        sx={{
                            borderRadius: 999, px: 2.5, flexShrink: 0, color: "#9FE08C",
                            border: "1px solid rgba(123,217,95,.35)",
                            "&:hover": { bgcolor: "rgba(46,125,32,.18)" },
                        }}>
                        Create Token
                    </Button>
                </Stack>

                {/*
                  * The gap between paying and the plan changing.
                  *
                  * Only after a payment started here, and only while something is still
                  * unpaid. It does not claim the money has arrived - the webhook decides
                  * that - it says the page is watching, which is the part the customer
                  * cannot see for themselves.
                  */}
                {paying && pending && (
                    <Box sx={{
                        px: 2.5, py: 1.8, mb: 2.5, borderRadius: 2,
                        bgcolor: gaveUp ? "rgba(214,158,46,.14)" : "rgba(46,125,32,.16)",
                        border: `1px solid ${gaveUp ? "rgba(243,201,105,.3)" : "rgba(123,217,95,.3)"}`,
                    }}>
                        <Typography sx={{ fontSize: 14, color: "rgba(255,255,255,.88)" }}>
                            {gaveUp
                                ? "Still waiting on the payment. Reload this page in a minute, and tell us if it has not moved."
                                : "Waiting for payment. This page updates itself."}
                        </Typography>
                    </Box>
                )}

                {/* Square corners: at this width a 32px radius bowed the first and last
                    rows and the thing stopped reading as a table. */}
                <Box sx={{
                    borderRadius: 1.5, overflow: "hidden", overflowX: "auto",
                    bgcolor: "rgba(255,255,255,.04)", border: "1px solid rgba(255,255,255,.12)",
                }}>
                    {rows === null ? (
                        <Skeleton variant="rectangular" height={160} sx={{ bgcolor: "rgba(255,255,255,.06)" }} />
                    ) : rows.length === 0 ? (
                        /* Empty, with the one thing to do next - not a blank table that leaves
                           somebody wondering whether it failed to load. */
                        <Stack sx={{ alignItems: "center", py: 7, px: 3, textAlign: "center" }}>
                            <Box sx={{
                                width: 54, height: 54, borderRadius: 3, mb: 2,
                                display: "grid", placeItems: "center",
                                bgcolor: "rgba(255,255,255,.07)", color: "rgba(255,255,255,.5)",
                            }}><KeyIcon /></Box>
                            <Typography sx={{ fontWeight: 700, fontSize: 17 }}>No tokens yet</Typography>
                            <Typography sx={{ color: "rgba(255,255,255,.55)", fontSize: 14, mt: .8, maxWidth: 420 }}>
                                Create a token for a plant's SCADA admin email. Lite starts free,
                                and you can move it to Plus or Pro whenever you like.
                            </Typography>
                            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}
                                sx={{ borderRadius: 999, px: 3, mt: 3 }}>Create Token</Button>
                        </Stack>
                    ) : (
                        <Table size="small" sx={{ tableLayout: "fixed", minWidth: 760 }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={th} align="center">Actions</TableCell>
                                    <TableCell sx={th} align="center">Email</TableCell>
                                    <TableCell sx={th} align="center">Plan</TableCell>
                                    <TableCell sx={th} align="center">Billing</TableCell>
                                    <TableCell sx={th} align="center">Key</TableCell>
                                    <TableCell sx={th} align="center">Status</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.map((r) => (
                                    <TableRow key={r.id} hover>
                                        {/* One button per row rather than a row of them.
                                            Three icons side by side turn the first column
                                            into a puzzle - and two of the three only apply
                                            sometimes, so the row would change width as
                                            plans come and go. */}
                                        <TableCell sx={td} align="center">
                                            <Stack direction="row" spacing={.6}
                                                sx={{ justifyContent: "center" }}>
                                                <Tooltip title="Edit token">
                                                    <IconButton sx={{ p: .3 }}
                                                        onClick={() => setEditing({
                                                            id: r.id, email: r.email,
                                                            plan: r.plan, currentPlan: r.plan,
                                                            currentStatus: r.status,
                                                            period: r.billing?.period || "monthly",
                                                            currentPeriod: r.billing?.period || "monthly",
                                                            status: r.status,
                                                            paidTill: r.billing?.paidTill || null,
                                                        })}>
                                                        <Avatar sx={actionAvatar()}>
                                                            <EditOutlinedIcon sx={{ fontSize: 16 }} />
                                                        </Avatar>
                                                    </IconButton>
                                                </Tooltip>

                                                {/* Only while there is something to stop, and
                                                    in red: this one ends a paid plan. */}
                                                {/* Anything that would charge again, not
                                                    only a live one. A mandate authorised
                                                    for a future date is also a future
                                                    charge, and a customer who scheduled
                                                    a change must be able to call it off
                                                    - otherwise the only way out is to
                                                    wait for the money to go. */}
                                                {["active", "pending"].includes(r.billing?.state)
                                                    && r.billing?.subscriptionId && (
                                                    <Tooltip title={scheduled(r)
                                                        ? "Cancel the scheduled change"
                                                        : "Cancel subscription"}>
                                                        <IconButton sx={{ p: .3 }} onClick={() => setStopping(r)}>
                                                            <Avatar sx={actionAvatar("#8C2F2F")}>
                                                                <CancelIcon sx={{ fontSize: 16 }} />
                                                            </Avatar>
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Stack>
                                        </TableCell>
                                        <TableCell sx={{ ...td, fontWeight: 600 }} align="center">{r.email}</TableCell>
                                        <TableCell sx={td} align="center">
                                            <Chip size="small" label={r.plan} sx={planChip(r.plan)} />
                                        </TableCell>

                                        {/* Where the money stands. The actions moved to the
                                            icons on the left, so this says one thing. */}
                                        <TableCell sx={td} align="center">
                                            {/* "Pending" covers both: a first payment not
                                                made yet, and a renewal Razorpay is still
                                                retrying. Either way the money has not
                                                arrived and the link is how to fix it. */}
                                            {/* A mandate that is authorised but not due
                                                yet is not pending in any sense the
                                                customer would recognise - they have
                                                paid, and the next charge is months out.
                                                Said as a date, and not as a button,
                                                because there is nothing to press. */}
                                            {r.billing?.state === "pending" && scheduled(r) ? (
                                                <Typography sx={{ fontSize: 13, color: "#9FE08C" }}>
                                                    Starts {fmtDate(r.billing.startsAt)}
                                                </Typography>
                                            ) : r.billing?.state === "pending" ? (
                                                <Button size="small" onClick={() => resumePay(r)}
                                                    sx={{ color: "#F3C969", fontSize: 13 }}>
                                                    Pending
                                                </Button>
                                            ) : r.billing?.state === "active" ? (
                                                /* What is actually taken, tax included - this
                                                   is the figure on the customer's statement,
                                                   and the plan price without it would never
                                                   match. The breakdown is on the invoice. */
                                                <Tooltip title={`₹${r.billing.amount?.toLocaleString("en-IN")} + GST`}>
                                                    <Typography sx={{ fontSize: 13, color: "#9FE08C" }}>
                                                        ₹{withGst(r.billing.amount, r.billing.gstRate || undefined).total.toLocaleString("en-IN")} / {r.billing.period === "yearly" ? "yr" : "mo"}
                                                    </Typography>
                                                </Tooltip>
                                            ) : r.billing?.state === "halted" ? (
                                                <Button size="small" onClick={() => resumePay(r)}
                                                    sx={{ color: "#FF9B9B", fontSize: 13 }}>
                                                    Payment failed
                                                </Button>
                                            ) : sellable.length ? (
                                                <Button size="small" onClick={() => openUpgrade(r)}
                                                    sx={{
                                                        borderRadius: 999, px: 2, fontSize: 13, color: "#9FE08C",
                                                        border: "1px solid rgba(123,217,95,.35)",
                                                    }}>
                                                    Upgrade
                                                </Button>
                                            ) : (
                                                <Typography sx={{ fontSize: 13, color: "rgba(255,255,255,.4)" }}>
                                                    free
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell sx={td} align="center">
                                            <Stack direction="row" spacing={.5} sx={{ alignItems: "center", justifyContent: "center" }}>
                                                <Typography sx={{ color: "rgba(255,255,255,.45)", fontFamily: "monospace", fontSize: 13 }}>
                                                    ••••{r.keyLast4}
                                                </Typography>
                                                <Tooltip title="Copy key">
                                                    <IconButton size="small" onClick={() => copyKey(r.id)}
                                                        sx={{ color: "rgba(255,255,255,.55)" }}>
                                                        <ContentCopyIcon sx={{ fontSize: 16 }} />
                                                    </IconButton>
                                                </Tooltip>
                                            </Stack>
                                        </TableCell>
                                        <TableCell sx={td} align="center">
                                            <Chip size="small" sx={statusChip(r.status)}
                                                label={r.status === "active" ? "Active" : "Deactive"} />
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </Box>

                {rows?.length > 0 && (
                    <Typography sx={{ fontSize: 13, color: "rgba(255,255,255,.45)", mt: 2 }}>
                        A token cannot be deleted. Set it to Deactive to stop that plant being
                        answered - you can turn it back on at any time.
                    </Typography>
                )}
            </Box>

            {/* ---------- create ---------- */}
            <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth
                slotProps={{ paper: dialogPaper }}>
                <DialogTitle sx={{ pb: .5 }}>
                    <Typography sx={{ fontWeight: 800, fontSize: 20 }}>New token</Typography>
                    <Typography sx={{ color: "rgba(255,255,255,.55)", fontSize: 13.5, mt: .3 }}>
                        One token per plant. The AI answers that plant on this allowance.
                    </Typography>
                </DialogTitle>

                <DialogContent sx={{ pt: 1 }}>
                    <Stack spacing={3} sx={{ mt: 1 }}>
                        <TextField label="Email" size="small" fullWidth sx={darkField}
                            value={form.email} autoFocus
                            onChange={(e) => setForm({ ...form, email: e.target.value })}
                            placeholder="admin@plant.com" />

                        {/*
                          * Plans as cards, not a dropdown.
                          *
                          * The allowance is the whole product and a dropdown hides it behind a
                          * click - somebody picking "Plus" should be able to see what Plus is
                          * and what the other two cost without opening anything.
                          */}
                        <Box>
                            <Typography sx={sectionLabel}>Plan</Typography>
                            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2}>
                                {allPlans.map((p) => {
                                    const on = form.plan === p.code;
                                    const price = p.priceMonthly
                                        ? `₹${p.priceMonthly.toLocaleString("en-IN")}/mo`
                                        : "Free";
                                    return (
                                        <Box key={p.code} onClick={() => setForm({ ...form, plan: p.code })}
                                            sx={{
                                                flex: 1, p: 1.6, borderRadius: 2, cursor: "pointer",
                                                bgcolor: on ? "rgba(46,125,32,.22)" : "rgba(255,255,255,.04)",
                                                border: `1px solid ${on ? "#7BD95F" : "rgba(255,255,255,.14)"}`,
                                                transition: "border-color .15s, background-color .15s",
                                                "&:hover": { borderColor: on ? "#7BD95F" : "rgba(255,255,255,.3)" },
                                            }}>
                                            <Typography sx={{ fontWeight: 700, fontSize: 14.5 }}>{p.name}</Typography>
                                            <Typography sx={{ fontSize: 13, color: on ? "#9FE08C" : "rgba(255,255,255,.6)" }}>
                                                {price}
                                            </Typography>
                                            <Typography sx={{ fontSize: 11.5, color: "rgba(255,255,255,.45)", mt: .4 }}>
                                                {(p.apiLimitPerDay / 100000).toFixed(p.apiLimitPerDay >= 1000000 ? 0 : 1)}
                                                {" "}lakh tokens/day
                                            </Typography>
                                        </Box>
                                    );
                                })}
                            </Stack>
                        </Box>

                        {/* Only for a paid plan: a billing period on a free one is a question
                            with no consequence. */}
                        {paidPlan && (
                            <Box>
                                <Typography sx={sectionLabel}>Billing</Typography>
                                <Stack direction="row" spacing={1.2}>
                                    {[
                                        ["monthly", "Monthly", paidPlan.priceMonthly, "per month", null],
                                        ["yearly", "Yearly", paidPlan.priceYearly, "per year",
                                            yearlySavingPct(paidPlan) ? `Save ${yearlySavingPct(paidPlan)}%` : null],
                                    ].map(([value, label, amount, unit, badge]) => {
                                        const on = form.period === value;
                                        return (
                                            <Box key={value} onClick={() => setForm({ ...form, period: value })}
                                                sx={{
                                                    flex: 1, p: 1.6, borderRadius: 2, cursor: "pointer",
                                                    position: "relative",
                                                    bgcolor: on ? "rgba(46,125,32,.22)" : "rgba(255,255,255,.04)",
                                                    border: `1px solid ${on ? "#7BD95F" : "rgba(255,255,255,.14)"}`,
                                                    "&:hover": { borderColor: on ? "#7BD95F" : "rgba(255,255,255,.3)" },
                                                }}>
                                                <Stack direction="row" spacing={1}
                                                    sx={{ alignItems: "center", mb: .3 }}>
                                                    <Typography sx={{ fontWeight: 700, fontSize: 14.5 }}>{label}</Typography>
                                                    {badge && (
                                                        <Chip size="small" label={badge} sx={{
                                                            height: 18, fontSize: 10.5, fontWeight: 700,
                                                            bgcolor: "rgba(123,217,95,.2)", color: "#9FE08C",
                                                        }} />
                                                    )}
                                                </Stack>
                                                <Typography sx={{ fontWeight: 700, fontSize: 17 }}>
                                                    ₹{amount?.toLocaleString("en-IN")}
                                                </Typography>
                                                <Typography sx={{ fontSize: 11.5, color: "rgba(255,255,255,.45)" }}>
                                                    {unit}
                                                    {value === "yearly" && (
                                                        <Box component="span" sx={{ textDecoration: "line-through", ml: .8 }}>
                                                            ₹{(paidPlan.priceMonthly * 12).toLocaleString("en-IN")}
                                                        </Box>
                                                    )}
                                                </Typography>
                                            </Box>
                                        );
                                    })}
                                </Stack>

                                <Typography sx={{ fontSize: 12.5, color: "rgba(255,255,255,.5)", mt: 1.2 }}>
                                    Razorpay opens next. The plan switches on once the payment goes
                                    through, and renews on its own until you cancel.
                                </Typography>
                            </Box>
                        )}

                        {/* Last, and small: a new token is almost always active, and the one
                            case that is not belongs out of the way rather than in the middle
                            of the purchase. */}
                        <Box>
                            <Typography sx={sectionLabel}>Status</Typography>
                            <StatusRadio value={form.status}
                                onChange={(v) => setForm({ ...form, status: v })} />
                        </Box>
                    </Stack>
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button onClick={() => setCreateOpen(false)} sx={{ color: "rgba(255,255,255,.65)" }}>
                        Cancel
                    </Button>
                    {/* Not "Pay": nothing is charged here. A paid plan goes on to the
                        checkout, where the amount with tax is shown and agreed to. */}
                    <Button variant="contained" disabled={!form.email || busy} onClick={create}
                        sx={{ borderRadius: 999, px: 3 }}>
                        {paidPlan ? "Continue" : "Create"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ---------- upgrade ---------- */}
            <Dialog open={Boolean(buying)} onClose={() => setBuying(null)} maxWidth="xs" fullWidth
                slotProps={{ paper: dialogPaper }}>
                <DialogTitle sx={{ fontWeight: 700 }}>Upgrade this plant</DialogTitle>
                <DialogContent>
                    <Typography sx={{ color: "rgba(255,255,255,.6)", fontSize: 14, mb: 2.5 }}>
                        {buying?.email}
                    </Typography>

                    <Stack spacing={2.5}>
                        {/*
                          * Every plan, including the one it is already on.
                          *
                          * Choosing the same plan is not always pointless: it is how a
                          * cancelled subscription is carried on, and how somebody moves
                          * from monthly to yearly billing. What is pointless - the same
                          * plan, the same period, on a live mandate - is stopped at the
                          * button below instead, where it can say why.
                          */}
                        <TextField select label="Plan" size="small" fullWidth sx={darkField}
                            value={choice.plan}
                            onChange={(e) => setChoice({ ...choice, plan: e.target.value })}>
                            {/* Just the name. The allowance is on the Plans page and on
                                the checkout that follows, and a seven-digit number in a
                                dropdown is read as noise rather than as a figure. */}
                            {sellable.map((p) => (
                                <MenuItem key={p.code} value={p.code}>
                                    {p.name}{p.code === buying?.plan ? "  (current)" : ""}
                                </MenuItem>
                            ))}
                        </TextField>

                        <TextField select label="Billing" size="small" fullWidth sx={darkField}
                            value={choice.period}
                            onChange={(e) => setChoice({ ...choice, period: e.target.value })}>
                            <MenuItem value="monthly">Monthly</MenuItem>
                            <MenuItem value="yearly">Yearly</MenuItem>
                        </TextField>

                        {/* The plan's own price, before tax. The full bill is on the next
                            step, where there is room to show how it is made up. */}
                        {(() => {
                            const amount = priceOf(choice.plan, choice.period);
                            return (
                                <Typography sx={{ fontSize: 15, color: amount ? "#fff" : "#F3C969" }}>
                                    {amount
                                        ? `₹${amount.toLocaleString("en-IN")} per ${choice.period === "yearly" ? "year" : "month"}, plus GST`
                                        : "This plan is not priced for that billing period yet."}
                                </Typography>
                            );
                        })()}

                        {/* Same warning as the edit dialog, for the same reason. */}
                        {stillPaidTill(buying?.billing?.paidTill) && (
                            <Box sx={{
                                px: 2, py: 1.3, borderRadius: 2,
                                bgcolor: "rgba(214,158,46,.14)", border: "1px solid rgba(243,201,105,.3)",
                            }}>
                                <Typography sx={{ fontSize: 13, color: "rgba(255,255,255,.85)" }}>
                                    Paid until {fmtDate(buying.billing.paidTill)}. Moving up starts a new
                                    period today and replaces those days; moving down or carrying on
                                    keeps them. The next screen says which.
                                </Typography>
                            </Box>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button onClick={() => setBuying(null)} sx={{ color: "rgba(255,255,255,.65)" }}>Cancel</Button>
                    {/* Nothing would change: same plan, same billing, and a mandate
                        already running. Said on the button rather than by hiding the
                        choice, so the reason is visible. */}
                    <Button variant="contained"
                        disabled={!priceOf(choice.plan, choice.period) || busy || nothingToChange}
                        onClick={reviewOrder}
                        sx={{ borderRadius: 999, px: 3 }}>
                        {nothingToChange ? "Already on this plan" : "Continue"}
                    </Button>
                </DialogActions>
            </Dialog>


            {/* ---------- the key, shown once ---------- */}
            <Dialog open={Boolean(newKey)} onClose={() => setNewKey(null)} maxWidth="sm" fullWidth
                slotProps={{ paper: dialogPaper }}>
                <DialogTitle sx={{ fontWeight: 700 }}>
                    <Stack direction="row" spacing={1.2} sx={{ alignItems: "center" }}>
                        <CheckCircleIcon sx={{ color: "#7BD95F" }} /> Token created
                    </Stack>
                </DialogTitle>
                <DialogContent>
                    <Typography sx={{ color: "rgba(255,255,255,.7)", fontSize: 14.5, mb: 2 }}>
                        Copy this key into the SCADA of <b style={{ color: "#fff" }}>{newKey?.email}</b>.
                        It is shown only now and cannot be read again.
                    </Typography>

                    <Stack direction="row" spacing={1} sx={{
                        alignItems: "center", p: 1.5, borderRadius: 2,
                        bgcolor: "rgba(0,0,0,.35)", border: "1px solid rgba(255,255,255,.14)",
                    }}>
                        <Typography sx={{ fontFamily: "monospace", fontSize: 13, wordBreak: "break-all", flex: 1 }}>
                            {newKey?.key}
                        </Typography>
                        <IconButton onClick={() => copy(newKey.key)} sx={{ color: "#7BD95F" }}>
                            <ContentCopyIcon fontSize="small" />
                        </IconButton>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button variant="contained" onClick={() => setNewKey(null)} sx={{ borderRadius: 999, px: 3 }}>
                        Done
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ---------- edit ---------- */}
            <Dialog open={Boolean(editing)} onClose={() => setEditing(null)} maxWidth="sm" fullWidth
                slotProps={{ paper: dialogPaper }}>
                <DialogTitle sx={{ pb: .5 }}>
                    <Typography sx={{ fontWeight: 800, fontSize: 20 }}>Edit token</Typography>
                    <Typography sx={{ color: "rgba(255,255,255,.55)", fontSize: 13.5, mt: .3 }}>
                        {editing?.email}
                    </Typography>
                </DialogTitle>

                <DialogContent sx={{ pt: 1 }}>
                    <Stack spacing={3} sx={{ mt: 1 }}>
                        {/* The same cards as the create dialog, because it is the same
                            decision - and a plan change here goes through the same payment,
                            whether it is Lite to Pro or Plus to Pro. */}
                        <Box>
                            <Typography sx={sectionLabel}>Plan</Typography>
                            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2}>
                                {allPlans.map((p) => {
                                    const on = editing?.plan === p.code;
                                    const current = editing?.currentPlan === p.code;
                                    return (
                                        <Box key={p.code}
                                            onClick={() => setEditing({ ...editing, plan: p.code })}
                                            sx={{
                                                flex: 1, p: 1.6, borderRadius: 2, cursor: "pointer",
                                                bgcolor: on ? "rgba(46,125,32,.22)" : "rgba(255,255,255,.04)",
                                                border: `1px solid ${on ? "#7BD95F" : "rgba(255,255,255,.14)"}`,
                                                "&:hover": { borderColor: on ? "#7BD95F" : "rgba(255,255,255,.3)" },
                                            }}>
                                            <Stack direction="row" spacing={.8} sx={{ alignItems: "center" }}>
                                                <Typography sx={{ fontWeight: 700, fontSize: 14.5 }}>{p.name}</Typography>
                                                {current && (
                                                    <Chip size="small" label="current" sx={{
                                                        height: 17, fontSize: 10, fontWeight: 600,
                                                        bgcolor: "rgba(255,255,255,.1)", color: "rgba(255,255,255,.65)",
                                                    }} />
                                                )}
                                            </Stack>
                                            <Typography sx={{ fontSize: 13, color: on ? "#9FE08C" : "rgba(255,255,255,.6)" }}>
                                                {p.priceMonthly ? `₹${p.priceMonthly.toLocaleString("en-IN")}/mo` : "Free"}
                                            </Typography>
                                            <Typography sx={{ fontSize: 11.5, color: "rgba(255,255,255,.45)", mt: .4 }}>
                                                {(p.apiLimitPerDay / 100000).toFixed(p.apiLimitPerDay >= 1000000 ? 0 : 1)}
                                                {" "}lakh tokens/day
                                            </Typography>
                                        </Box>
                                    );
                                })}
                            </Stack>
                        </Box>

                        {editPaidPlan && (
                            <Box>
                                <Typography sx={sectionLabel}>Billing</Typography>
                                <Stack direction="row" spacing={1.2}>
                                    {[
                                        ["monthly", "Monthly", editPaidPlan.priceMonthly, "per month", null],
                                        ["yearly", "Yearly", editPaidPlan.priceYearly, "per year",
                                            yearlySavingPct(editPaidPlan) ? `Save ${yearlySavingPct(editPaidPlan)}%` : null],
                                    ].map(([value, label, amount, unit, badge]) => {
                                        const on = (editing?.period || "monthly") === value;
                                        return (
                                            <Box key={value}
                                                onClick={() => setEditing({ ...editing, period: value })}
                                                sx={{
                                                    flex: 1, p: 1.6, borderRadius: 2, cursor: "pointer",
                                                    bgcolor: on ? "rgba(46,125,32,.22)" : "rgba(255,255,255,.04)",
                                                    border: `1px solid ${on ? "#7BD95F" : "rgba(255,255,255,.14)"}`,
                                                    "&:hover": { borderColor: on ? "#7BD95F" : "rgba(255,255,255,.3)" },
                                                }}>
                                                <Stack direction="row" spacing={1} sx={{ alignItems: "center", mb: .3 }}>
                                                    <Typography sx={{ fontWeight: 700, fontSize: 14.5 }}>{label}</Typography>
                                                    {badge && (
                                                        <Chip size="small" label={badge} sx={{
                                                            height: 18, fontSize: 10.5, fontWeight: 700,
                                                            bgcolor: "rgba(123,217,95,.2)", color: "#9FE08C",
                                                        }} />
                                                    )}
                                                </Stack>
                                                <Typography sx={{ fontWeight: 700, fontSize: 17 }}>
                                                    ₹{amount?.toLocaleString("en-IN")}
                                                </Typography>
                                                <Typography sx={{ fontSize: 11.5, color: "rgba(255,255,255,.45)" }}>
                                                    {unit}
                                                </Typography>
                                            </Box>
                                        );
                                    })}
                                </Stack>

                                {planChanged && (
                                    <Typography sx={{ fontSize: 12.5, color: "rgba(255,255,255,.5)", mt: 1.2 }}>
                                        Razorpay opens next. The new plan starts once that payment goes
                                        through{editing?.currentPlan !== "lite"
                                            ? " - the subscription you are on now is cancelled first, so you are never charged twice."
                                            : "."}
                                    </Typography>
                                )}

                                {/* Said before they pay, not discovered afterwards: the
                                    days left on what they already bought do not move to
                                    the new period. */}
                                {planChanged && stillPaidTill(editing?.paidTill) && (
                                    <Box sx={{
                                        px: 2, py: 1.3, mt: 1.5, borderRadius: 2,
                                        bgcolor: "rgba(214,158,46,.14)", border: "1px solid rgba(243,201,105,.3)",
                                    }}>
                                        <Typography sx={{ fontSize: 13, color: "rgba(255,255,255,.85)" }}>
                                            Paid until {fmtDate(editing.paidTill)}. Moving up starts a new
                                            period today and replaces those days; moving down keeps them.
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        )}

                        {/* Dropping to Lite is not a purchase, so it says what it does instead
                            of showing a price. */}
                        {planChanged && editing?.plan === "lite" && (
                            <Box sx={{
                                px: 2, py: 1.5, borderRadius: 2,
                                bgcolor: "rgba(214,158,46,.14)", border: "1px solid rgba(243,201,105,.3)",
                            }}>
                                <Typography sx={{ fontSize: 13.5, color: "rgba(255,255,255,.85)" }}>
                                    Billing stops. The plan you have paid for keeps running until the
                                    end of the period, then drops to Lite.
                                </Typography>
                            </Box>
                        )}

                        <Box>
                            <Typography sx={sectionLabel}>Status</Typography>
                            <StatusRadio value={editing?.status || "active"}
                                onChange={(v) => setEditing({ ...editing, status: v })} />
                        </Box>
                    </Stack>
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button onClick={() => setEditing(null)} sx={{ color: "rgba(255,255,255,.65)" }}>Cancel</Button>
                    <Button variant="contained" disabled={busy} onClick={saveEdit}
                        sx={{ borderRadius: 999, px: 3 }}>
                        {/* The amount is settled on the checkout now - it depends on the
                            direction of the move and on days already paid for - so the
                            button here only says where it leads. */}
                        {editIsPurchase ? "Continue" : "Save"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ---------- stop billing, asked first ---------- */}
            <Dialog open={Boolean(stopping)} onClose={() => setStopping(null)} maxWidth="xs" fullWidth
                slotProps={{ paper: dialogPaper }}>
                <DialogTitle sx={{ fontWeight: 700 }}>
                    {scheduled(stopping || {}) ? "Call off the scheduled change?" : "Cancel this subscription?"}
                </DialogTitle>
                <DialogContent>
                    <Typography sx={{ color: "rgba(255,255,255,.6)", fontSize: 14, mb: 1.5 }}>
                        {stopping?.email}
                    </Typography>

                    {/* Two different acts, and the difference decides whether somebody
                        should press it: one undoes a change that has not happened, the
                        other ends a plan they are using. */}
                    <Typography sx={{ color: "rgba(255,255,255,.8)", fontSize: 14.5 }}>
                        {scheduled(stopping || {})
                            ? `The ${stopping?.billing?.pendingPlan || "new"} plan due to start on ${fmtDate(stopping?.billing?.startsAt)} will not begin, and nothing will be charged for it.`
                            : "No further payments will be taken."}
                        {stillPaidTill(stopping?.billing?.paidTill)
                            ? ` Your ${stopping?.plan} plan keeps working until ${fmtDate(stopping?.billing?.paidTill)}, and drops to Lite after that.`
                            : " The plan drops to Lite."}
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button onClick={() => setStopping(null)} sx={{ color: "rgba(255,255,255,.65)" }}>
                        Keep it
                    </Button>
                    <Button variant="contained" color="error" disabled={busy}
                        onClick={() => stopSubscription(stopping)}
                        sx={{ borderRadius: 999, px: 3 }}>
                        {scheduled(stopping || {}) ? "Call it off" : "Cancel subscription"}
                    </Button>
                </DialogActions>
            </Dialog>

            <AppFooter />
        </Box>
    );
}
